<template>
	<div>
		<b-breadcrumb>
			<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
			<b-breadcrumb-item active>Timers</b-breadcrumb-item>
		</b-breadcrumb>
		<div class="text-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<b-alert show v-else-if="error" variant="danger">{{error&&(error.error||error.toString())}}</b-alert>
		<div v-else-if="timers?.length > 0">
			<b-list-group>
				<b-list-group-item v-for="(timer, idx) in timers" :key="idx" class="d-flex align-items-center timer-item">
					<div class="flex-fill">
						<b-form-checkbox switch v-model="timer.status" value="enabled" unchecked-value="disabled" @input="setTimerStatus(timer, timer.status)" :disabled="timer.changingStatus">
							{{timer.name}}
							<span class="text-muted">
								Every {{Math.round(timer.interval_min)}} minutes
								{{timer.delay_min ? `, starting after ${Math.round(timer.delay_min)} minute${timer.delay_min == 1 ? '' : 's'}` : ''}}
							</span>
						</b-form-checkbox>
						<span class="text-muted">{{timer.messages?.[0]}}</span>
					</div>
					<b-button-group size="sm">
						<b-button @click="openEditModal(timer.id)" v-b-tooltip.hover.left title="Edit timer"><b-icon icon="pencil-fill"></b-icon></b-button>
						<b-button variant="danger" @click="openDeleteModal(timer.id)" v-b-tooltip.hover.right.v-danger title="Delete timer"><b-icon icon="trash"></b-icon></b-button>
					</b-button-group>
				</b-list-group-item>
			</b-list-group>
			<div class="text-center mt-3">
				<b-button @click="resetTimers()" variant="secondary" class="mr-2"><b-icon icon="bootstrap-reboot"></b-icon> Restart all timers</b-button>
				<b-button @click="openEditModal()" variant="success"><b-icon icon="plus"></b-icon> Add a timer</b-button>
			</div>
		</div>
		<b-card v-else class="text-center text-muted p-5">No timers have been added yet.<br />Timers are a way to send periodic messages to your verified channels' chats when you are live.<br /><br /><br /><b-button @click="openEditModal()" variant="success"><b-icon icon="plus"></b-icon> Add a timer</b-button><br /></b-card>
		<b-modal ref="editModal" title="Edit user timer" :busy="loading>0">
			<b-form @submit.stop.prevent="save(); return false" id="userTimerEditForm">
				<b-form-group label="Name">
					<b-form-input v-model="curTimer.name" required></b-form-input>
				</b-form-group>
				<b-form-row>
					<b-col>
						<b-form-group label="Interval">
							<b-input-group append="minutes">
								<b-form-input type="number" min="1" step="1" v-model="curTimer.interval_min" required></b-form-input>
							</b-input-group>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Delay">
							<b-input-group append="minutes">
								<b-form-input type="number" min="0" step="1" v-model="curTimer.delay_min"></b-form-input>
							</b-input-group>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-group label="Messages">
					<b-textarea v-model="curTimer.messages" required></b-textarea>
					<b-form-text>Enter one message per line. Messages posted will be chosen randomly from this list. Messages are only posted when the stream is live.</b-form-text>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!error">{{error}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<b-spinner v-show="loading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="userTimerEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
		<b-modal ref="deleteModal" title="Delete timer" @ok="deleteItem()">
			<span v-if="curTimer && curTimer.name">Are you sure you want to delete {{curTimer.name}}?</span>
			<b-alert variant="danger" :show="!!error">{{error}}</b-alert>
			<template #modal-footer="{ok, cancel}">
				<b-spinner v-show="loading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="danger" @click="ok()">
					Delete
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
export default {
	name: 'Timers',
	data() { return {
		timers: [],
		error: null,
		loading: 0,
		curTimer: {},
		statuses: [ 'enabled', 'disabled' ],
	}},
	created() {
		this.loadTimers();
	},
	methods: {
		loadTimers() {
			this.loading++;
			this.error = null;
			this.$api.get('timers').then(timers => this.timers = timers).catch(err => this.error = err).finally(() => this.loading--);
		},
		openEditModal(id) {
			if(id) {
				this.loading++;
				this.$api.get('timer', { id }).then(timer => {
					timer.messages = (timer.messages||[]).join('\n');
					this.$set(this, 'curTimer', timer);
				}).catch(err => {
					this.error = err.toString();
				}).finally(() => {
					this.loading--;
				});
			} else {
				this.$set(this, 'curTimer', {
					id: null,
					name: 'timed message',
					interval_min: 5,
					delay_min: 0,
					messages: ''
				});
			}
			this.error = null;
			this.$refs.editModal.show();
		},
		openDeleteModal(id) {
			this.loading++;
			this.$api.get('timer', { id }).then(timer => {
				this.$set(this, 'curTimer', timer);
			}).catch(err => {
				this.error = err.toString();
			}).finally(() => {
				this.loading--;
			});
			this.$refs.deleteModal.show();
		},
		save() {
			this.loading++;
			let data = {
				name: this.curTimer.name,
				interval_min: this.curTimer.interval_min,
				delay_min: this.curTimer.delay_min,
				messages: (this.curTimer.messages||'').split('\n').map(a => a.replace(/^\s*(.*?)\s*$/, '$1')).filter(a => !!a),
			};
			this.$api.post('timer', { id: this.curTimer.id, data }).then(() => {
				this.$refs.editModal.hide();
				this.$bvToast.toast('Successfully saved', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
			}).catch(err => {
				this.error = err&&err.error||err.toString();
				this.saving = false;
				console.error('Error saving item:', err);
			}).finally(() => {
				this.loadTimers();
				this.loading--;
			});
		},
		deleteItem() {
			this.loading++;
			this.error = null;
			this.$api.delete('timer', { id: this.curTimer.id }).then(() => {
				this.$refs.deleteModal.hide();
				this.$bvToast.toast('Deleted timer', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.loadTimers();
			}).catch(err => {
				this.error = err&&err.error||err.toString();
				console.error('Error deleting timer:', err);
			}).finally(() => {
				this.loading--;
			});
		},
		setTimerStatus(item, status) {
			this.$set(item, 'changingStatus', true);
			this.error = null;
			this.$api.post('timerStatus', { id: item.id, status }).then(() => {
				this.error = null;
				this.$set(item, 'changingStatus', false);
			}).catch(err => {
				this.error = err.toString();
				console.error('Could not set timer status', item.id, status, err);
			});
		},
		resetTimers() {
			this.$api.post('resetTimers').then(() => {
				this.$bvToast.toast('All timers reset', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
			}).catch(e => {
				this.$bvToast.toast('Could not reset all timers: '+(e.error||String(e)), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			});
		},
	},
};
</script>

<style scoped lang="scss">
@import '../plugins/variable.scss';

.timer-item {
	background-color: $gray-200;
}
.timer-item:nth-child(odd) {
	background-color: $gray-400;
}
.timer-item:hover {
	background-color: $gray-600;
}
</style>
